import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useFetcher } from 'react-router'
import { toast } from 'sonner'
import { Button } from '#app/components/ui/button.tsx'
import {
	Drawer,
	DrawerContent,
	DrawerHeader,
	DrawerTitle,
	DrawerDescription,
	DrawerFooter,
} from '#app/components/ui/drawer.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { Label } from '#app/components/ui/label.tsx'
import {
	type ExhibitionSearchProvider,
	type ExhibitionSearchResult,
} from '#app/utils/exhibition-search.server.ts'

interface ExhibitionDrawerProps {
	isOpen: boolean
	onClose: () => void
	userId: string
}

export function ExhibitionDrawer({ isOpen, onClose }: ExhibitionDrawerProps) {
	const [searchQuery, setSearchQuery] = useState('')
	const [currentIndex, setCurrentIndex] = useState(0)
	const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(
		null,
	)

	const searchFetcher = useFetcher<{
		exhibitions: ExhibitionSearchResult[]
		provider?: ExhibitionSearchProvider
	}>()
	const addFetcher = useFetcher()

	const isSearching = searchFetcher.state !== 'idle'
	const isAdding = addFetcher.state !== 'idle'

	// Get current results
	const exhibitions = searchFetcher.data?.exhibitions || []
	const currentExhibition = exhibitions[currentIndex]
	const searchProvider = searchFetcher.data?.provider

	// Navigation helpers
	const hasMultipleResults = exhibitions.length > 1
	const canGoPrevious = currentIndex > 0
	const canGoNext = currentIndex < exhibitions.length - 1

	// Debounced search
	useEffect(() => {
		if (searchQuery.length > 2) {
			if (debounceTimer) clearTimeout(debounceTimer)

			const timer = setTimeout(() => {
				setCurrentIndex(0) // Reset to first result
				void searchFetcher.submit(
					{ query: searchQuery },
					{
						method: 'POST',
						action: '/resources/exhibition-search',
					},
				)
			}, 1000)

			setDebounceTimer(timer)
		}

		return () => {
			if (debounceTimer) clearTimeout(debounceTimer)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchQuery])

	const handleAdd = () => {
		if (!currentExhibition) return

		void addFetcher.submit(
			{
				intent: 'create',
				title: currentExhibition.title,
				type: currentExhibition.type,
				address: currentExhibition.address || '',
				beginDate: currentExhibition.beginDate || '',
				endDate: currentExhibition.endDate || '',
				openingTimes: JSON.stringify(currentExhibition.openingTimes || []),
				googleMapsUrl: currentExhibition.googleMapsUrl || '',
				websiteUrl: currentExhibition.websiteUrl || '',
				description: currentExhibition.description || '',
			},
			{
				method: 'POST',
				action: '/resources/exhibitions',
			},
		)
	}

	// Reset state when drawer closes
	const handleClose = () => {
		setSearchQuery('')
		setCurrentIndex(0)
		onClose()
	}

	// Close drawer when submission is successful
	useEffect(() => {
		if (addFetcher.data?.success) {
			toast.success('Expositie toegevoegd', {
				description:
					'De beschrijving en vertalingen worden op de achtergrond aangevuld.',
			})
			handleClose()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addFetcher.data])

	return (
		<Drawer open={isOpen} onOpenChange={handleClose}>
			<DrawerContent className="max-h-[90vh]">
				<DrawerHeader>
					<DrawerTitle>Voeg expositie of galerie toe</DrawerTitle>
					<DrawerDescription>
						Zoek een expositie of galerie en voeg deze toe aan je lijst
					</DrawerDescription>
				</DrawerHeader>

				<div className="space-y-4 overflow-y-auto px-4 pb-4">
					{/* Search Input */}
					<div className="space-y-2">
						<Label htmlFor="search">Zoek expositie of galerie</Label>
						<Input
							id="search"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							placeholder="bijv. Stedelijk Museum Amsterdam"
						/>
					</div>

					{/* Loading state */}
					{isSearching && (
						<div className="bg-card rounded-lg border p-6">
							<div className="flex items-center justify-center gap-2">
								<Loader2 className="h-4 w-4 animate-spin" />
								<span>Zoeken...</span>
							</div>
						</div>
					)}

					{/* No results */}
					{!isSearching &&
						searchQuery.length > 2 &&
						exhibitions.length === 0 && (
							<div className="bg-card rounded-lg border p-6">
								<p className="text-muted-foreground text-center">
									Geen resultaten voor "{searchQuery}"
								</p>
							</div>
						)}

					{/* Search Result */}
					{currentExhibition && (
						<div className="relative space-y-3 rounded-lg border p-4">
							{/* Navigation buttons */}
							{hasMultipleResults && (
								<div className="absolute top-4 right-4 flex gap-2">
									<span className="text-muted-foreground mr-2 text-sm">
										{currentIndex + 1} / {exhibitions.length}
									</span>
									<Button
										variant="outline"
										size="icon"
										onClick={() => setCurrentIndex(currentIndex - 1)}
										disabled={!canGoPrevious}
										className="h-8 w-8"
									>
										<ChevronLeft className="h-4 w-4" />
									</Button>
									<Button
										variant="outline"
										size="icon"
										onClick={() => setCurrentIndex(currentIndex + 1)}
										disabled={!canGoNext}
										className="h-8 w-8"
									>
										<ChevronRight className="h-4 w-4" />
									</Button>
								</div>
							)}

							<h3 className="pr-32 text-lg font-semibold">
								{currentExhibition.title}
							</h3>

							<div className="grid gap-2 text-sm">
								<div>
									<span className="font-medium">Type:</span>{' '}
									{currentExhibition.type === 'gallery'
										? 'Galerie'
										: 'Expositie'}
								</div>

								{currentExhibition.address && (
									<div>
										<span className="font-medium">Adres:</span>{' '}
										{currentExhibition.address}
									</div>
								)}

								{currentExhibition.beginDate && (
									<div>
										<span className="font-medium">Begin:</span>{' '}
										{currentExhibition.beginDate}
									</div>
								)}

								{currentExhibition.endDate && (
									<div>
										<span className="font-medium">Einde:</span>{' '}
										{currentExhibition.endDate}
									</div>
								)}

								{currentExhibition.openingTimes &&
									currentExhibition.openingTimes.length > 0 && (
										<div>
											<span className="font-medium">Openingstijden:</span>
											<div className="ml-2">
												{currentExhibition.openingTimes.map((time, index) => (
													<div key={index}>{time}</div>
												))}
											</div>
										</div>
									)}

								{currentExhibition.description && (
									<div>
										<span className="font-medium">Beschrijving:</span>
										<p className="mt-1">{currentExhibition.description}</p>
									</div>
								)}

								<div className="flex gap-4 pt-2">
									{currentExhibition.websiteUrl && (
										<a
											href={currentExhibition.websiteUrl}
											target="_blank"
											rel="noopener noreferrer"
											className="text-blue-600 hover:underline"
										>
											Website →
										</a>
									)}
									{currentExhibition.googleMapsUrl && (
										<a
											href={currentExhibition.googleMapsUrl}
											target="_blank"
											rel="noopener noreferrer"
											className="text-blue-600 hover:underline"
										>
											Maps →
										</a>
									)}
								</div>
							</div>
						</div>
					)}

					{/* Provider visibility: a dead/missing key must never silently degrade */}
					{!isSearching && searchQuery.length > 2 && searchProvider && (
						<p
							className={
								searchProvider === 'gemini'
									? 'text-muted-foreground text-xs'
									: 'text-xs text-amber-600'
							}
						>
							{searchProvider === 'gemini'
								? 'Live zoekresultaten via Google'
								: 'Resultaten uit AI-geheugen — voeg GEMINI_API_KEY toe voor live zoeken (details kunnen verouderd zijn)'}
						</p>
					)}
				</div>

				<DrawerFooter>
					<div className="flex justify-end gap-2">
						<Button variant="outline" onClick={handleClose}>
							Annuleren
						</Button>
						{currentExhibition && (
							<Button onClick={handleAdd} disabled={isAdding}>
								{isAdding ? 'Toevoegen...' : 'Voeg toe'}
							</Button>
						)}
					</div>
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	)
}
